import { useState } from "react";
import {
  useLoaderData,
  useNavigate
} from "react-router-dom";
import { fetchClient } from '../../util/fetchClient.js';
import ConfirmationModal from "../_Shared/ConfirmationModal.js";

const pageLimit = 10;

const fetchBranches= async ({ offset=0, limit=pageLimit }) => {
  const { body: branches } = await fetchClient(`/v1/branches?offset=${offset}&limit=${limit}`);
  return { branches: branches.data };
};

export const loader = () => fetchBranches({ offset: 0, limit: pageLimit });

const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ')
};

export default function Branches() {
  const loaderData = useLoaderData();

  const navigate = useNavigate();
  const [lastPage, setLastPage] = useState(Infinity);
  const [currentPage, setCurrentPage] = useState(1);
  const [branches, setBranches] = useState(loaderData.branches);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const newButtonClick = () => navigate('/branches/new');

  const editButtonClick = (id) => navigate(`/branches/edit/${id}`);

  const deleteButtonClick = async (branch) => {
    setSelectedBranch(branch);
    setShowDeleteModal(true);
  };

  const deleteHandler = async () => {
    await fetchClient(`/v1/branches/${selectedBranch.id}`, { method: 'DELETE' });
    setShowDeleteModal(false);
    window.location.reload();
  };

  const cancelHandler = () => {
    setShowDeleteModal(false);
  };

  const isLastPage = () => lastPage <= currentPage || branches.length < pageLimit || branches.length === 0;

  const handlePreviousPage = async () => {
    if (currentPage === 1) {
      return;
    }

    const newPage = currentPage - 1;
    const pageOffset = newPage * pageLimit - pageLimit;
    const { branches: updatedBranches } = await fetchBranches({ offset: pageOffset, limit: pageLimit });
    setCurrentPage(currentPage - 1);
    setBranches(updatedBranches);
    setLastPage(Infinity);
  };

  const handleNextPage = async () => {
    if (isLastPage()) {
      return;
    }

    const newPage = currentPage + 1;
    const pageOffset = newPage * pageLimit - pageLimit;
    const { branches: updatedBranches } = await fetchBranches({ offset: pageOffset, limit: pageLimit });
    if (updatedBranches.length) {
      setCurrentPage(newPage);
      setBranches(updatedBranches);
      setLastPage(Infinity);
    } else {
      setLastPage(currentPage);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {showDeleteModal && ( <ConfirmationModal deleteHandler={deleteHandler} cancelHandler={cancelHandler}/> )}

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto">
            <h1 className="mt-3 text-base font-semibold leading-6 text-gray-900 -mr-5">Lista de Sucursales</h1>
          </div>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button" 
            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={newButtonClick} >
            Nueva
          </button>
        </div>
      </div>

      <div className="mt-5 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="top-0 z-10 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Nombre
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Estado
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Ciudad
                    </th>
                    <th
                      scope="col"
                      className="hidden md:block top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Domicilio
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      C.P.
                    </th>
                    <th>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {branches.map((branch, _branchIdx) => (
                    <tr key={branch.id}>
                      <td
                        className={classNames(
                          'whitespace-nowrap py-3 pl-2 pr-3 text-sm font-medium text-gray-900 sm:pl-6'
                        )}
                      >
                        {branch.name}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {branch.address_state || 'N/A'}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {branch.address_city || 'N/A'}
                      </td>
                      <td
                        className={classNames(
                          'hidden md:block overflow-hidden whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {branch.address_line1 || 'N/A'}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {branch.address_postal_code || 'N/A'}
                      </td>
                      <td
                        className={classNames(
                          'relative whitespace-nowrap py-3 text-right font-medium pr-5'
                        )}
                      >
                        <button type="button" className="text-indigo-600 hover:text-indigo-900" onClick={() => editButtonClick(branch.id)}>Editar</button>
                        <button type="button" className="text-rose-600 hover:text-rose-900 pl-5" onClick={() => deleteButtonClick(branch)}>Borrar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <nav
        className="flex items-center justify-between bg-white px-4 py-4 sm:px-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Página <span className="font-medium">{currentPage}</span>, mostrando <span className="font-medium">{branches.length}</span> resultados
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button
            disabled={currentPage === 1}
            onClick={handlePreviousPage}
            className='disabled:bg-gray-300 disabled:opacity-50 relative inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0'
          >
            Anterior
          </button>
          <button
            disabled={isLastPage()}
            onClick={handleNextPage}
            className='disabled:bg-gray-300 disabled:opacity-50 relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0'
          >
            Siguiente
          </button>
        </div>
      </nav>
    </div>
  )
}
